/**
 * PlatformShell — minimal layout for /platform/* routes.
 *
 * Distinct from the tenant AppShell: dark sidebar, compact nav, no tenant
 * banners/modals. Only platform admins ever see this shell; `PlatformRoute`
 * in App.tsx gates access.
 */

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Divider, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AccountBalance, Assessment, AutoAwesome, Business, CallSplit, Description,
  Email, ExitToApp, Flag, HistoryEdu,
  HourglassEmpty, Insights, Logout, Menu as MenuIcon, Payment, Router,
  SupervisorAccount, TrendingDown, VpnKey,
} from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { PLATFORM_SHELL_COLORS, NEUTRAL_COLORS } from '@/theme/colorTokens';
import ImpersonationBanner from '@/components/ImpersonationBanner';

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
  // When set, only roles in this list see the entry. Undefined = visible to
  // every platform role. The backend still enforces access; this just hides
  // 403-destined links from the sidebar to match what each role can do.
  roles?: string[];
};

const NAV: NavItem[] = [
  { label: 'Overview', to: '/platform/overview', icon: <Assessment />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Tenants', to: '/platform/tenants', icon: <Business />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Trials', to: '/platform/trials', icon: <HourglassEmpty />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Dunning', to: '/platform/dunning', icon: <TrendingDown />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Invoices', to: '/platform/invoices', icon: <Payment />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Gateways', to: '/platform/gateways', icon: <Router />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Gateway Keys', to: '/platform/billing/gateways', icon: <VpnKey />, roles: ['platform_finance', 'platform_owner'] },
  { label: 'Emails', to: '/platform/emails', icon: <Email />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Analytics', to: '/platform/analytics', icon: <Insights />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Attribution', to: '/platform/attribution', icon: <CallSplit />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Flags', to: '/platform/flags', icon: <Flag />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Content', to: '/platform/content', icon: <Description />, roles: ['platform_marketing', 'platform_owner'] },
  { label: 'AI Pricing', to: '/platform/ai-pricing', icon: <AutoAwesome />, roles: ['platform_finance', 'platform_owner'] },
  { label: 'AI Keys', to: '/platform/ai-keys', icon: <VpnKey />, roles: ['platform_owner'] },
  { label: 'SSO / Identity', to: '/platform/auth/sso', icon: <VpnKey />, roles: ['platform_owner'] },
  { label: 'AI Prompts', to: '/platform/prompts', icon: <AutoAwesome />, roles: ['platform_owner'] },
  { label: 'Embeddings', to: '/platform/embeddings', icon: <AutoAwesome />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Plans', to: '/platform/plans', icon: <AccountBalance />, roles: ['platform_owner'] },
  { label: 'Country Pricing', to: '/platform/pricing', icon: <AccountBalance />, roles: ['platform_readonly', 'platform_finance', 'platform_owner'] },
  { label: 'SaaS Gateway', to: '/platform/billing/gateways', icon: <Payment />, roles: ['platform_finance', 'platform_owner'] },
  { label: 'SaaS Books', to: '/platform/saas-books', icon: <AccountBalance />, roles: ['platform_finance', 'platform_owner'] },
  { label: 'Audit', to: '/platform/audit', icon: <HistoryEdu />, roles: ['platform_readonly', 'platform_support', 'platform_finance', 'platform_owner'] },
  { label: 'Admins', to: '/platform/admins', icon: <SupervisorAccount />, roles: ['platform_owner'] },
];

const SIDEBAR_WIDTH = 232;

export default function PlatformShell() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const platformRole = useAuthStore((s) => s.platformRole);
  const companies = useAuthStore((s) => s.companies);
  const logout = useAuthStore((s) => s.logout);
  // Hide "Back to tenant" when the platform admin has no tenant memberships —
  // navigating to /dashboard would hit an RLS-empty view.
  const hasTenantMembership = companies.length > 0;

  const handleSignOut = async () => {
    try { await authApi.logout(); } catch { /* already signed out */ }
    logout();
    navigate('/login', { replace: true });
  };

  const drawerContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        bgcolor: PLATFORM_SHELL_COLORS.sidebarBg,
        color: PLATFORM_SHELL_COLORS.sidebarFg,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="overline" sx={{ color: PLATFORM_SHELL_COLORS.sidebarLabel, letterSpacing: '0.12em' }}>
          AnooreHR
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: PLATFORM_SHELL_COLORS.sidebarLinkActiveFg }}>
          Platform Admin
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />
      <List sx={{ px: 1.5, flex: 1 }}>
        {NAV.filter((item) =>
          !item.roles || !platformRole || item.roles.includes(platformRole),
        ).map((item) => (
          <ListItem key={item.to} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={NavLink}
              to={item.to}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                borderRadius: 1.5,
                color: PLATFORM_SHELL_COLORS.sidebarLinkFg,
                '&.active': {
                  bgcolor: 'rgba(59, 130, 246, 0.12)',
                  color: PLATFORM_SHELL_COLORS.sidebarLinkActiveFg,
                  '& .MuiListItemIcon-root': { color: PLATFORM_SHELL_COLORS.sidebarLinkActiveFg },
                },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: PLATFORM_SHELL_COLORS.sidebarLabel, display: 'block' }}>
          Role
        </Typography>
        <Typography variant="body2" sx={{ color: PLATFORM_SHELL_COLORS.sidebarLinkActiveFg, fontWeight: 500 }}>
          {platformRole?.replace('platform_', '') ?? '—'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: PLATFORM_SHELL_COLORS.mainBg }}>
      {/* Mobile top bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ bgcolor: PLATFORM_SHELL_COLORS.sidebarBg, zIndex: (t) => t.zIndex.drawer + 1 }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Open navigation menu"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Platform Admin
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flex: 1,
          pt: isMobile ? 7 : 0,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              bgcolor: NEUTRAL_COLORS.white,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.25,
                borderRadius: 1,
                bgcolor: PLATFORM_SHELL_COLORS.sidebarBg,
                color: PLATFORM_SHELL_COLORS.sidebarLinkActiveFg,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.1em',
              }}
            >
              PLATFORM CONSOLE
            </Box>
            <Box sx={{ flex: 1 }} />
            {hasTenantMembership && (
              <Tooltip title="Back to your tenant workspace">
                <IconButton size="small" aria-label="Back to tenant workspace" onClick={() => navigate('/dashboard')}>
                  <ExitToApp fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              aria-label="Open account menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: PLATFORM_SHELL_COLORS.sidebarBg }}>
                {user?.first_name?.[0] ?? 'A'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem disabled>
                <Typography variant="body2">{user?.email}</Typography>
              </MenuItem>
              <Divider />
              {hasTenantMembership && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate('/dashboard');
                  }}
                >
                  <ListItemIcon>
                    <ExitToApp fontSize="small" />
                  </ListItemIcon>
                  Back to tenant workspace
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleSignOut();
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Box>
        )}

        <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
          <ImpersonationBanner />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

